import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LogIn, LogOut, Loader2, KeyRound, Check, X, BookUser, ExternalLink, UserRoundCog, Upload, Download, FileText, Eye } from "lucide-react";
import ConsolaSolicitudes from "@/pages/ConsolaSolicitudes";
import SolicitudCotizacion from "@/pages/SolicitudCotizacion";
import DocumentPreviewDialog from "@/components/DocumentPreviewDialog";

// Editor del Perfil de Viajero desde el Portal Cliente.
// Sólo campos que el propio viajero puede tocar; el backend registra cada
// cambio en `traveler_profiles.actualizado_por[]` con `via: 'portal_cliente'`.
const PerfilViajeroEditor = ({ open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [perfil, setPerfil] = useState(null);
  const [form, setForm] = useState({});
  const [docFields, setDocFields] = useState([]); // catálogo de campos file del proyecto
  const [uploading, setUploading] = useState(null); // key del campo en subida
  const [preview, setPreview] = useState(null); // { url, filename, label }

  const loadPerfil = async () => {
    try {
      const r = await axios.get(`${API}/forms/external/perfil`, { withCredentials: true });
      setPerfil(r.data);
      setForm({ ...(r.data?.datos || {}) });
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cargar el perfil");
      onClose();
    }
  };

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      await loadPerfil();
      try {
        const r = await axios.get(`${API}/forms/external/perfil/documentos-catalogo`, { withCredentials: true });
        setDocFields(r.data?.fields || []);
      } catch (_) { /* opcional */ }
      setLoading(false);
    })();
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadDoc = async (campo, file, ev) => {
    if (!file) return;
    setUploading(campo);
    try {
      const fd = new FormData();
      fd.append("campo", campo);
      fd.append("file", file);
      await axios.post(`${API}/forms/external/perfil/archivo`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Documento actualizado");
      await loadPerfil();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo subir el archivo");
    } finally {
      setUploading(null);
      if (ev?.target) ev.target.value = "";
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const r = await axios.get(`${API}/forms/external/perfil/archivo/${doc.id}`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.filename || "documento";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo descargar");
    }
  };

  const save = async () => {
    if (!perfil) return;
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/forms/external/perfil`, { updates: form }, { withCredentials: true });
      const n = (r.data?.changed || []).length;
      if (n === 0) toast.info("No hay cambios que guardar");
      else toast.success(`Perfil actualizado (${n} campo${n === 1 ? "" : "s"})`);
      onClose(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto overflow-x-hidden" data-testid="perfil-viajero-editor">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserRoundCog className="w-5 h-5 text-indigo-600" />
            Perfil de Viajero
            {perfil?.folio && <span className="text-xs font-mono text-slate-500 ml-2">{perfil.folio}</span>}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Cargando…
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-slate-500">
              Los cambios quedan registrados en la bitácora del perfil.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div><Label className="text-xs font-semibold uppercase text-slate-600">Nombre(s)</Label>
                <Input value={form.nombres || ""} onChange={set("nombres")} data-testid="perfil-nombres" /></div>
              <div><Label className="text-xs font-semibold uppercase text-slate-600">Apellido paterno</Label>
                <Input value={form.apellidoP || ""} onChange={set("apellidoP")} data-testid="perfil-apellidoP" /></div>
              <div><Label className="text-xs font-semibold uppercase text-slate-600">Apellido materno</Label>
                <Input value={form.apellidoM || ""} onChange={set("apellidoM")} data-testid="perfil-apellidoM" /></div>
              <div className="md:col-span-2"><Label className="text-xs font-semibold uppercase text-slate-600">Email</Label>
                <Input type="email" value={form.email || ""} onChange={set("email")} data-testid="perfil-email" /></div>
              <div><Label className="text-xs font-semibold uppercase text-slate-600">Nacionalidad</Label>
                <Input value={form.nacionalidad || ""} onChange={set("nacionalidad")} data-testid="perfil-nacionalidad" /></div>
              <div><Label className="text-xs font-semibold uppercase text-slate-600">Fecha de nacimiento</Label>
                <Input type="date" value={form.fechaNac || ""} onChange={set("fechaNac")} data-testid="perfil-fechaNac" /></div>
              <div><Label className="text-xs font-semibold uppercase text-slate-600">Teléfono celular</Label>
                <Input type="tel" value={form.telCel || ""} onChange={set("telCel")} data-testid="perfil-telCel" /></div>
              <div><Label className="text-xs font-semibold uppercase text-slate-600">Teléfono alterno</Label>
                <Input type="tel" value={form.telAlt || ""} onChange={set("telAlt")} data-testid="perfil-telAlt" /></div>
              <div className="md:col-span-2"><Label className="text-xs font-semibold uppercase text-slate-600">Dirección</Label>
                <Input value={form.direccion || ""} onChange={set("direccion")} data-testid="perfil-direccion" /></div>
              <div><Label className="text-xs font-semibold uppercase text-slate-600">Ciudad</Label>
                <Input value={form.ciudad || ""} onChange={set("ciudad")} data-testid="perfil-ciudad" /></div>
              <div><Label className="text-xs font-semibold uppercase text-slate-600">C.P.</Label>
                <Input value={form.cp || ""} onChange={set("cp")} data-testid="perfil-cp" /></div>
            </div>

            <div className="pt-2 border-t border-slate-200">
              <p className="text-sm font-semibold text-slate-700 mb-2">Contacto de emergencia</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div><Label className="text-xs font-semibold uppercase text-slate-600">Nombre</Label>
                  <Input value={form.emNombre || ""} onChange={set("emNombre")} data-testid="perfil-emNombre" /></div>
                <div><Label className="text-xs font-semibold uppercase text-slate-600">Apellido</Label>
                  <Input value={form.emApellido || ""} onChange={set("emApellido")} data-testid="perfil-emApellido" /></div>
                <div><Label className="text-xs font-semibold uppercase text-slate-600">Parentesco</Label>
                  <Input value={form.emParentesco || ""} onChange={set("emParentesco")} data-testid="perfil-emParentesco" /></div>
                <div><Label className="text-xs font-semibold uppercase text-slate-600">Teléfono</Label>
                  <Input type="tel" value={form.emTel || ""} onChange={set("emTel")} data-testid="perfil-emTel" /></div>
                <div className="md:col-span-2"><Label className="text-xs font-semibold uppercase text-slate-600">Email</Label>
                  <Input type="email" value={form.emEmail || ""} onChange={set("emEmail")} data-testid="perfil-emEmail" /></div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label className="text-xs font-semibold uppercase text-slate-600">Alergias / condiciones médicas</Label>
                <Textarea rows={2} value={form.alergias || ""} onChange={set("alergias")} data-testid="perfil-alergias" /></div>
              <div><Label className="text-xs font-semibold uppercase text-slate-600">Comentarios</Label>
                <Textarea rows={2} value={form.comentarios || ""} onChange={set("comentarios")} data-testid="perfil-comentarios" /></div>
            </div>

            {/* Documentos: subida, reemplazo y descarga */}
            {docFields.length > 0 && (
              <div className="pt-2 border-t border-slate-200">
                <p className="text-sm font-semibold text-slate-700 mb-2">Documentos</p>
                <p className="text-xs text-slate-500 mb-2">
                  Sube o reemplaza tus documentos oficiales (identificaciones, visas, etc.). Imagen o PDF, máximo 10&nbsp;MB.
                </p>
                <div className="space-y-2">
                  {docFields.map((f) => {
                    const doc = (perfil?.documentos || []).find((d) => d.campo === f.k || d.tipo === f.k);
                    const isUploading = uploading === f.k;
                    return (
                      <div key={f.k} className="flex items-center gap-2 border border-slate-200 rounded px-3 py-2 bg-slate-50">
                        <FileText className="w-4 h-4 text-slate-500 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate">{f.col}</div>
                          <div className="text-xs text-slate-500 truncate">{doc ? doc.filename : "Sin documento cargado"}</div>
                        </div>
                        {doc && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7"
                              onClick={() => setPreview({
                                url: `${API}/forms/external/perfil/archivo/${doc.id}`,
                                filename: doc.filename,
                                label: f.col,
                              })}
                              data-testid={`portal-ver-${f.k}`}
                              title="Vista previa"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7"
                              onClick={() => downloadDoc(doc)}
                              data-testid={`portal-dl-${f.k}`}
                              title="Descargar"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <input
                          type="file"
                          id={`portal-up-${f.k}`}
                          className="hidden"
                          accept="image/*,application/pdf"
                          onChange={(e) => uploadDoc(f.k, e.target.files?.[0], e)}
                          data-testid={`portal-up-input-${f.k}`}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7"
                          onClick={() => document.getElementById(`portal-up-${f.k}`).click()}
                          disabled={isUploading}
                          data-testid={`portal-up-${f.k}`}
                        >
                          {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1" />{doc ? "Reemplazar" : "Subir"}</>}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-3 border-t border-slate-200">
              <Button variant="outline" onClick={() => onClose(false)} disabled={saving}>Cancelar</Button>
              <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="perfil-save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                Guardar
              </Button>
            </div>
          </div>
        )}
        <DocumentPreviewDialog
          open={!!preview}
          onClose={() => setPreview(null)}
          url={preview?.url}
          filename={preview?.filename}
          label={preview?.label}
          testIdPrefix="portal-perfil-doc-preview"
        />
      </DialogContent>
    </Dialog>
  );
};

// Regla → función booleana
const PW_RULES = [
  { id: "len", label: "Al menos 8 caracteres", test: (p) => (p || "").length >= 8 },
  { id: "upper", label: "Una letra mayúscula (A-Z)", test: (p) => /[A-Z]/.test(p || "") },
  { id: "lower", label: "Una letra minúscula (a-z)", test: (p) => /[a-z]/.test(p || "") },
  { id: "digit", label: "Un número (0-9)", test: (p) => /\d/.test(p || "") },
  { id: "special", label: "Un carácter especial (!@#$…)", test: (p) => /[^A-Za-z0-9]/.test(p || "") },
];

const strengthScore = (pw) => PW_RULES.reduce((n, r) => n + (r.test(pw) ? 1 : 0), 0);
const strengthMeta = (pw) => {
  const n = strengthScore(pw);
  if (n <= 2) return { pct: (n / 5) * 100, color: "bg-red-500", text: "Débil", tone: "text-red-600" };
  if (n <= 4) return { pct: (n / 5) * 100, color: "bg-amber-500", text: "Media", tone: "text-amber-600" };
  return { pct: 100, color: "bg-emerald-500", text: "Fuerte", tone: "text-emerald-600" };
};

const ClientePortal = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [slugInfo, setSlugInfo] = useState(null);
  const [slugError, setSlugError] = useState("");
  const [authed, setAuthed] = useState(null); // { email, nombre_completo, empresa, slug }
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Cambio obligatorio/opcional de contraseña
  const [mustChange, setMustChange] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwSubmitting, setPwSubmitting] = useState(false);

  // Gate: perfil de viajero requerido
  const [needsPerfil, setNeedsPerfil] = useState(false);
  const [perfilCheckLoading, setPerfilCheckLoading] = useState(false);
  const perfilPollRef = useRef(null);
  // Editor del perfil desde el portal
  const [perfilEditorOpen, setPerfilEditorOpen] = useState(false);

  /** Consulta /me y decide qué mostrar. Devuelve el payload de /me. */
  const refreshMeAndRoute = async () => {
    const me = await axios.get(`${API}/forms/external/me`, { withCredentials: true });
    const d = me.data || {};
    setAuthed(d);
    if (d.must_change_password) {
      setMustChange(true);
      setNeedsPerfil(false);
      return d;
    }
    setMustChange(false);
    if (d.perfil_viajero_required && !d.perfil_viajero_completed) {
      setNeedsPerfil(true);
      return d;
    }
    setNeedsPerfil(false);
    return d;
  };

  // Polling mientras el usuario llena su perfil de viajero
  useEffect(() => {
    if (!needsPerfil) {
      if (perfilPollRef.current) { clearInterval(perfilPollRef.current); perfilPollRef.current = null; }
      return;
    }
    perfilPollRef.current = setInterval(async () => {
      try {
        const me = await axios.get(`${API}/forms/external/me`, { withCredentials: true });
        if (me.data?.perfil_viajero_completed) {
          setNeedsPerfil(false);
          setAuthed(me.data);
          toast.success("Perfil de viajero registrado. Ya puedes solicitar cotizaciones.");
        }
      } catch { /* ignore */ }
    }, 4000);
    return () => {
      if (perfilPollRef.current) { clearInterval(perfilPollRef.current); perfilPollRef.current = null; }
    };
  }, [needsPerfil]);

  const checkPerfilNow = async () => {
    setPerfilCheckLoading(true);
    try {
      const me = await axios.get(`${API}/forms/external/me`, { withCredentials: true });
      if (me.data?.perfil_viajero_completed) {
        setNeedsPerfil(false);
        setAuthed(me.data);
        toast.success("Perfil de viajero registrado. Ya puedes solicitar cotizaciones.");
      } else {
        toast.info("Aún no detectamos tu perfil. Termina de enviar el formulario e intenta de nuevo.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo verificar el perfil");
    } finally {
      setPerfilCheckLoading(false);
    }
  };

  // Check slug validity and current session
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const info = await axios.get(`${API}/forms/external/slug/${slug}`);
        setSlugInfo(info.data);
      } catch (err) {
        setSlugError(err.response?.data?.detail || "URL no válida");
        setLoading(false);
        return;
      }
      try {
        const me = await axios.get(`${API}/forms/external/me`, { withCredentials: true });
        if (me.data?.slug === slug) {
          setAuthed(me.data);
          if (me.data?.must_change_password) {
            setMustChange(true);
            setPwCurrent(""); // deben re-teclearla
          } else if (me.data?.perfil_viajero_required && !me.data?.perfil_viajero_completed) {
            setNeedsPerfil(true);
          }
        }
      } catch { /* not authed, show login */ }
      setLoading(false);
    })();
  }, [slug]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Ingresa tus credenciales"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/forms/external/login`, {
        slug, email: email.trim().toLowerCase(), password,
      }, { withCredentials: true });
      setAuthed(res.data);
      if (res.data?.must_change_password) {
        // Forzar cambio: precargo la actual con la que acabo de teclear
        setMustChange(true);
        setPwCurrent(password);
        setPwNew(""); setPwConfirm("");
        toast.info("Debes cambiar tu contraseña temporal antes de continuar");
      } else if (res.data?.perfil_viajero_required && !res.data?.perfil_viajero_completed) {
        setNeedsPerfil(true);
        toast.info(`Bienvenido ${res.data.nombre_completo}. Antes de cotizar completa tu perfil de viajero.`);
      } else if ((res.data?.user_type || "solicitante") === "administrador_solicitudes") {
        toast.success(`Bienvenido ${res.data.nombre_completo} · Consola de Solicitudes`);
      } else {
        toast.success(`Bienvenido ${res.data.nombre_completo}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar sesión");
    } finally { setSubmitting(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwNew !== pwConfirm) { toast.error("Las contraseñas no coinciden"); return; }
    if (strengthScore(pwNew) < PW_RULES.length) { toast.error("Cumple todos los requisitos primero"); return; }
    if (!pwCurrent) { toast.error("Ingresa tu contraseña actual"); return; }
    setPwSubmitting(true);
    try {
      await axios.post(`${API}/forms/external/change-password`, {
        current_password: pwCurrent,
        new_password: pwNew,
      }, { withCredentials: true });
      setMustChange(false);
      setPwCurrent(""); setPwNew(""); setPwConfirm("");
      // Refrescar authed y decidir siguiente paso (perfil o cotización)
      await refreshMeAndRoute();
      toast.success("Contraseña actualizada correctamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo actualizar");
    } finally {
      setPwSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/forms/external/logout`, {}, { withCredentials: true });
    } catch { /* ignore */ }
    setAuthed(null);
    setEmail(""); setPassword("");
    setNeedsPerfil(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (slugError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-3">
            <div className="w-14 h-14 mx-auto bg-red-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">⚠️</span>
            </div>
            <h1 className="text-xl font-bold text-slate-800">URL inválida</h1>
            <p className="text-sm text-slate-500">{slugError}</p>
            <p className="text-xs text-slate-400">Verifica el enlace que te compartieron o contacta a tu representante PD Travel.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 p-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="flex items-center justify-center gap-3 mb-4">
                <img
                  src="https://pdtravelmex.com/wp-content/uploads/2026/03/Diseno-sin-titulo-52.png"
                  alt="PD Travel"
                  className="h-10 w-auto"
                  data-testid="login-pd-logo"
                />
                {slugInfo?.client_logo_url && (
                  <>
                    <span className="h-8 w-px bg-slate-300" aria-hidden="true" />
                    <img
                      src={slugInfo.client_logo_url}
                      alt={slugInfo.empresa}
                      className="h-10 w-auto max-w-[120px] object-contain"
                      data-testid="login-client-logo"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  </>
                )}
              </div>
              <h1 className="text-2xl font-bold text-slate-800">Portal de solicitudes</h1>
              <p className="text-sm text-slate-500 mt-1">
                <strong>{slugInfo?.empresa}</strong>
              </p>
            </div>
            <form onSubmit={handleLogin} className="space-y-3">
              <div>
                <Label>Usuario (email)</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus data-testid="login-email" />
              </div>
              <div>
                <Label>Contraseña</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
              </div>
              <Button type="submit" disabled={submitting} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="login-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LogIn className="w-4 h-4 mr-2" />}
                Ingresar
              </Button>
            </form>
            <p className="text-xs text-slate-400 text-center mt-4">
              ¿No tienes acceso? Contacta a tu representante PD Travel.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (authed && mustChange) {
    const meta = strengthMeta(pwNew);
    const ruleStatus = PW_RULES.map((r) => ({ ...r, ok: r.test(pwNew) }));
    const allOk = ruleStatus.every((r) => r.ok);
    const matches = pwNew && pwNew === pwConfirm;
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                <KeyRound className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Cambiar contraseña</h1>
                <p className="text-sm text-slate-500">
                  Por seguridad, define una contraseña personal antes de continuar.
                </p>
              </div>
            </div>
            <form onSubmit={handleChangePassword} className="space-y-3.5" autoComplete="off">
              <div>
                <Label>Contraseña actual (temporal)</Label>
                <Input
                  type="password"
                  value={pwCurrent}
                  onChange={(e) => setPwCurrent(e.target.value)}
                  autoFocus
                  required
                  data-testid="pw-current"
                />
              </div>
              <div>
                <Label>Nueva contraseña</Label>
                <Input
                  type="password"
                  value={pwNew}
                  onChange={(e) => setPwNew(e.target.value)}
                  required
                  data-testid="pw-new"
                />
                {pwNew && (
                  <>
                    <div className="mt-2 h-1.5 w-full bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${meta.color} transition-all duration-300`}
                        style={{ width: `${meta.pct}%` }}
                        data-testid="pw-strength-bar"
                      />
                    </div>
                    <div className="mt-1 flex items-center justify-between text-xs">
                      <span className={`font-medium ${meta.tone}`} data-testid="pw-strength-label">
                        Nivel de seguridad: {meta.text}
                      </span>
                      <span className="text-slate-400">{strengthScore(pwNew)}/{PW_RULES.length}</span>
                    </div>
                  </>
                )}
              </div>
              <ul className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-1.5" data-testid="pw-rules">
                {ruleStatus.map((r) => (
                  <li
                    key={r.id}
                    className={`flex items-center gap-2 text-sm transition-colors ${r.ok ? "text-emerald-700" : "text-slate-500"}`}
                    data-testid={`pw-rule-${r.id}`}
                  >
                    {r.ok ? (
                      <Check className="w-4 h-4 text-emerald-600 shrink-0" />
                    ) : (
                      <X className="w-4 h-4 text-slate-400 shrink-0" />
                    )}
                    <span className={r.ok ? "font-medium" : ""}>{r.label}</span>
                  </li>
                ))}
              </ul>
              <div>
                <Label>Confirmar nueva contraseña</Label>
                <Input
                  type="password"
                  value={pwConfirm}
                  onChange={(e) => setPwConfirm(e.target.value)}
                  required
                  data-testid="pw-confirm"
                />
                {pwConfirm && !matches && (
                  <p className="mt-1 text-xs text-red-600">Las contraseñas no coinciden.</p>
                )}
              </div>
              <Button
                type="submit"
                disabled={pwSubmitting || !allOk || !matches || !pwCurrent}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="pw-submit"
              >
                {pwSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Guardar contraseña
              </Button>
            </form>
            <p className="text-xs text-slate-400 text-center mt-4">
              ¿Problemas para actualizar? Contacta a tu representante PD Travel.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Gate: perfil de viajero requerido y aún no completado
  if (authed && needsPerfil) {
    const projectSlug = authed.perfil_viajero_project_slug || "";
    const projectNombre = authed.perfil_viajero_project_nombre || "";
    const perfilUrl = projectSlug ? `${window.location.origin}/viajero/${projectSlug}` : "";
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col">
        <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <img
              src="https://pdtravelmex.com/wp-content/uploads/2026/03/Diseno-sin-titulo-52.png"
              alt="PD Travel"
              className="h-9 w-auto"
            />
            {authed.client_logo_url && (
              <>
                <span className="h-6 w-px bg-slate-200" aria-hidden="true" />
                <img
                  src={authed.client_logo_url}
                  alt={authed.empresa}
                  className="h-7 w-auto max-w-[90px] object-contain"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              </>
            )}
            <div>
              <p className="text-sm font-semibold text-slate-800">{authed.empresa}</p>
              <p className="text-xs text-slate-500">{authed.nombre_completo} · {authed.email}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
          </Button>
        </header>
        <div className="border-b border-indigo-200 bg-indigo-50 px-4 py-3">
          <div className="max-w-6xl mx-auto flex items-start gap-3 flex-wrap">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
              <BookUser className="w-5 h-5 text-indigo-700" />
            </div>
            <div className="flex-1 min-w-[240px]">
              <p className="text-sm font-semibold text-indigo-900">Antes de tu primera cotización debes completar tu perfil de viajero</p>
              <p className="text-xs text-indigo-700 mt-0.5">
                Proyecto: <b>{projectNombre || "—"}</b>. Al enviar el formulario detectaremos automáticamente tu perfil y habilitaremos las cotizaciones.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {perfilUrl && (
                <a href={perfilUrl} target="_blank" rel="noreferrer">
                  <Button variant="outline" size="sm" className="border-indigo-400 text-indigo-700 hover:bg-indigo-100" data-testid="perfil-open-new-tab">
                    <ExternalLink className="w-4 h-4 mr-1.5" /> Abrir en pestaña nueva
                  </Button>
                </a>
              )}
              <Button
                size="sm"
                className="bg-indigo-600 hover:bg-indigo-700"
                onClick={checkPerfilNow}
                disabled={perfilCheckLoading}
                data-testid="perfil-check-now"
              >
                {perfilCheckLoading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
                Ya completé mi perfil
              </Button>
            </div>
          </div>
        </div>
        <main className="flex-1">
          {perfilUrl ? (
            <iframe
              src={perfilUrl}
              title="Perfil de Viajero"
              className="w-full border-0"
              style={{ height: "calc(100vh - 140px)" }}
              data-testid="perfil-viajero-iframe"
            />
          ) : (
            <div className="p-6">
              <div className="max-w-2xl mx-auto bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                <p className="font-semibold mb-1">Perfil no configurado</p>
                <p>El proyecto asociado a tu acceso no está disponible. Contacta a tu representante PD Travel.</p>
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <img
            src="https://pdtravelmex.com/wp-content/uploads/2026/03/Diseno-sin-titulo-52.png"
            alt="PD Travel"
            className="h-9 w-auto"
            data-testid="cliente-portal-logo"
          />
          {authed.client_logo_url && (
            <>
              <span className="h-6 w-px bg-slate-200" aria-hidden="true" />
              <img
                src={authed.client_logo_url}
                alt={authed.empresa}
                className="h-7 w-auto max-w-[90px] object-contain"
                data-testid="cliente-portal-client-logo"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            </>
          )}
          <div>
            <p className="text-sm font-semibold text-slate-800">{authed.empresa}</p>
            <p className="text-xs text-slate-500">{authed.nombre_completo} · {authed.email}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {authed.perfil_viajero_completed && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPerfilEditorOpen(true)}
              data-testid="btn-open-perfil-viajero"
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
            >
              <UserRoundCog className="w-4 h-4 mr-2" /> Perfil de Viajero
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
          </Button>
        </div>
      </header>
      <main className="flex-1">
        {(authed.user_type || "solicitante") === "administrador_solicitudes" ? (
          <ConsolaSolicitudes authed={authed} />
        ) : (
          <SolicitudCotizacion authed={authed} onLogout={handleLogout} />
        )}
      </main>
      <PerfilViajeroEditor
        open={perfilEditorOpen}
        onClose={async (changed) => {
          setPerfilEditorOpen(false);
          if (changed) {
            // Refrescar authed (aunque los cambios están en el perfil, no en el acceso,
            // así el próximo mount del SolicitudCotizacion refetch de perfil también.)
            try {
              const me = await axios.get(`${API}/forms/external/me`, { withCredentials: true });
              setAuthed(me.data);
            } catch (_) { /* noop */ }
          }
        }}
      />
    </div>
  );
};

export default ClientePortal;
